import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, videos, featuredVideos, newVideos } from '@/mocks/videos';

function VideoCard({ video }: { video: typeof videos[number] }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          title={`${video.title} - 久久视频高清在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-medium text-foreground-50 bg-black/70 rounded-md">
          {video.duration}
        </span>
        {video.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium text-background-50 bg-primary-500 rounded-md">
            热门
          </span>
        )}
        {video.isNew && !video.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium text-background-50 bg-accent-500 rounded-md">
            最新
          </span>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
            <i className="ri-play-fill text-2xl text-background-50"></i>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {video.title}
        </h3>
        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500"></i>
            {video.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}

function CategoryCard({ category }: { category: typeof categories[number] }) {
  return (
    <Link
      to={`/category/${category.slug}`}
      className="group flex flex-col items-center p-6 rounded-lg bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
    >
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
        <i className={`${category.icon} text-2xl text-primary-600`}></i>
      </div>
      <span className="mt-3 text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors whitespace-nowrap">
        {category.name}
      </span>
      <span className="mt-1 text-xs text-foreground-500">{category.count}+ 部</span>
    </Link>
  );
}

export default function Home() {
  const [currentFaq, setCurrentFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: '久久视频需要付费吗？',
      a: '久久视频是一个完全免费的在线视频平台，所有内容均可免费观看，无需注册、无需付费。我们致力于为用户提供最优质、最便捷的视频观看体验。',
    },
    {
      q: '久久视频支持哪些设备观看？',
      a: '久久视频支持PC端、平板端和手机端观看，适配各种主流浏览器。无论您使用什么设备，都可以流畅享受高清视频内容。我们推荐使用Chrome、Edge、Safari等现代浏览器获得最佳体验。',
    },
    {
      q: '久久视频的视频画质如何？',
      a: '久久视频提供高清（HD）画质播放，部分热门内容支持超清（FHD）画质。视频加载速度快，播放流畅无卡顿，为您带来极致的观影体验。',
    },
    {
      q: '如何找到我想看的视频内容？',
      a: '您可以通过首页的分类导航浏览不同类型的内容，也可以使用搜索功能查找特定视频。我们还会根据热门趋势为您推荐优质内容，让您轻松发现感兴趣的精彩视频。',
    },
    {
      q: '久久视频的内容多久更新一次？',
      a: '久久视频每天都会更新大量新鲜内容，涵盖影视、综艺、动漫、短视频等多种类型。热门影视作品和综艺节目会在第一时间上线，确保您不会错过任何精彩瞬间。',
    },
  ];

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '久久视频',
    url: 'https://www.jiujiu-video.com/',
    description: '久久视频提供海量高清视频免费在线播放，涵盖影视、综艺、动漫、短视频等多种类型，流畅播放无卡顿，随时随地畅享久久视频带来的精彩内容体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.jiujiu-video.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '久久视频',
    url: 'https://www.jiujiu-video.com/',
    description: '久久视频提供海量高清视频免费在线播放，涵盖影视、综艺、动漫、短视频等多种类型。',
    sameAs: [
      'https://www.jiujiu-video.com/',
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(seoJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20cinematic%20abstract%20composition%20with%20flowing%20curved%20gradient%20shapes%20in%20warm%20coral%20and%20deep%20charcoal%20tones%2C%20dark%20elegant%20atmosphere%20with%20subtle%20particles%2C%20smooth%20silk-like%20texture%2C%20minimalist%20art%20style%2C%204K%20quality%2C%20professional%20broadcast%20design&width=1920&height=800&seq=hero-bg-main&orientation=landscape"
              alt="久久视频 - 海量高清视频免费在线观看"
              title="久久视频官网入口"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-4 leading-tight">
                海量高清视频
                <br />
                <strong className="text-primary-400">免费在线观看</strong>
              </h1>
              <p className="text-base md:text-lg text-foreground-200 mb-8 max-w-2xl mx-auto leading-relaxed">
                久久视频汇聚影视、综艺、动漫、短视频等多元内容，高清流畅播放无卡顿，随时随地畅享精彩视频体验
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <div className="relative w-full max-w-md">
                  <input
                    type="text"
                    placeholder="搜索你喜欢的视频内容..."
                    className="w-full pl-5 pr-12 py-3.5 text-sm bg-background-50/15 backdrop-blur-sm text-foreground-50 placeholder-foreground-300 border border-foreground-300/30 rounded-full focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 transition-all"
                  />
                  <button
                    type="button"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer"
                    aria-label="搜索"
                  >
                    <i className="ri-search-line text-foreground-50"></i>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-center gap-6 mt-8">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-50">12,000+</div>
                  <div className="text-xs text-foreground-300 mt-1">视频资源</div>
                </div>
                <div className="w-px h-10 bg-foreground-300/30"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-50">8大</div>
                  <div className="text-xs text-foreground-300 mt-1">内容分类</div>
                </div>
                <div className="w-px h-10 bg-foreground-300/30"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-50">5000万+</div>
                  <div className="text-xs text-foreground-300 mt-1">月活用户</div>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  探索精彩内容
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  八大分类，总有一款适合你
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
                {categories.map((cat) => (
                  <CategoryCard key={cat.slug} category={cat} />
                ))}
              </div>
            </div>
          </section>

          {/* Featured/Hot Videos */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    热门推荐
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    大家都在看的精彩内容
                  </p>
                </div>
                <Link
                  to="/category/yingshi"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featuredVideos.slice(0, 8).map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Videos */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    最新上线
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    新鲜出炉的精彩内容，第一时间尝鲜
                  </p>
                </div>
                <Link
                  to="/category/duanshipin"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {newVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>

          {/* All Videos Grid */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  精选内容
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  精心挑选的优质视频内容，不容错过
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {videos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/category/yingshi"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部内容
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  为什么选择久久视频
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  以用户体验为核心，打造极致视频平台
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-hd-line', title: '高清画质', desc: '支持1080P高清播放，画面清晰细腻，带来影院级视觉享受' },
                  { icon: 'ri-speed-up-line', title: '极速加载', desc: '采用CDN加速技术，视频秒加载，播放流畅不卡顿' },
                  { icon: 'ri-smartphone-line', title: '多端适配', desc: 'PC、平板、手机全平台支持，随时随地想看就看' },
                  { icon: 'ri-price-tag-3-line', title: '完全免费', desc: '无需注册，无需付费，所有内容免费畅享' },
                ].map((feature) => (
                  <div key={feature.title} className="text-center p-6">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${feature.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <h3 className="mt-4 text-base font-semibold text-foreground-950">{feature.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20gradient%20background%20with%20warm%20coral%20and%20dark%20charcoal%20waves%2C%20elegant%20modern%20texture%2C%20smooth%20curves%2C%20minimalist%20design%2C%20professional%20broadcast%20style&width=1920&height=600&seq=cta-bg-home&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60"></div>
            <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">
                准备好开始探索了吗？
              </h2>
              <p className="text-base text-foreground-200 mb-8">
                加入久久视频，畅享海量高清视频内容
              </p>
              <Link
                to="/category/yingshi"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                立即探索
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  常见问题
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  关于久久视频的常见疑问解答
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setCurrentFaq(currentFaq === idx ? null : idx)}
                    >
                      <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${currentFaq === idx ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        currentFaq === idx ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}